import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";

import { useJsonApi } from "@/modules/api/hooks/useJsonApi";
import { useMutation } from "@/modules/api/hooks/useMutation";
import { queryKeys } from "@/modules/api/queryKeys";
import { apiErrorValidate } from "@/modules/api/utils/apiErrorValidate";
import { notify } from "@/modules/notify/notify";
import { completeSubtaskMutation } from "@/modules/tasks/api/mutations/completeSubtaskMutation";

const translationNamespace = "apiMessages/useCompleteSubtaskMutation";

export const useCompleteSubtaskMutation = (onSuccess?: () => void) => {
  const jsonApi = useJsonApi();
  const queryClient = useQueryClient();

  const { t } = useTranslation(translationNamespace);

  return useMutation([completeSubtaskMutation.name], {
    mutationFn: completeSubtaskMutation(jsonApi),
    onSuccess: async () => {
      await queryClient.invalidateQueries(queryKeys.tasks.getTasksList);
      notify.alert.success(t("The subtask has been completed successfully"));
      onSuccess && onSuccess();
    },
    onError: (error) => {
      apiErrorValidate(error, {
        401: {
          UNAUTHORIZED: t(
            "You are not allowed to view the subtask. Please sign in again"
          ),
        },
        403: {
          FORBIDDEN: t("You don't have an access to view the subtask"),
        },
        404: {
          SUBTASK_NOT_FOUND: t(
            "The subtask cannot be found. Please check if you have required permissions"
          ),
        },
        422: {
          SUBTASK_ALREADY_COMPLETED: t(
            "The subtask has already been completed. Please refresh the app"
          ),
        },
      });
    },
    retry: false,
  });
};
